import React, { FunctionComponent, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BaseButton, ButtonSize, ButtonType } from '../../atoms/baseButton/BaseButton'
import { HeadphoneHeartIcon } from '../../atoms/icons/HeadphoneHeartIcon'
import { SecondaryIconButton } from '../../atoms/secondaryIconButton/SecondaryIconButton'
import TertiaryIconButton from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { AuroraIconCircle } from '../../organisms/lightHomeToolbar/utils'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CareNavigatorHeaderEntryPointButtonProps {
  isInternational?: boolean
  onPress: () => void
  isActive?: boolean
  testID?: string
  suppressAccessibilitySelected?: boolean
  useSimplifiedCNIconWithTooltip?: boolean
  onIconHover?: ({ hoverText }: { hoverText: string }) => void
  lightBgColor?: boolean
}

const StyledBaseButton = styled(BaseButton)<{ theme: ThemeType }>(({ theme }) => ({
  justifyContent: 'center',
  paddingTop: theme.spacing['8px'],
  paddingBottom: theme.spacing['8px'],
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  borderWidth: 1.5,
  height: '40px', // intentionally specific height given by design
  borderColor: theme.colors.borderDefault,
}))

export const CareNavigatorHeaderEntryPointButton: FunctionComponent<CareNavigatorHeaderEntryPointButtonProps> = ({
  isInternational = false,
  onPress,
  onIconHover = noop,
  useSimplifiedCNIconWithTooltip = false,
  isActive = false,
  testID = tID('CareNavigatorHeaderEntryPoint-button'),
  lightBgColor,
  ...restProps
}) => {
  const { breakpoints, colors, isAuroraTheme } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const tooltipRef = useRef<{ show: () => void; hide: () => void }>(null)

  const buttonColorText = colors.tertiaryButtonTextActive
  const buttonColorIcon =
    isAuroraTheme && lightBgColor ? colors.iconDefault : colors.components.careNavigatorHeaderEntryPointButton.icon.fill
  const heartFillColor =
    isAuroraTheme && lightBgColor
      ? colors.iconDefault
      : colors.components.careNavigatorHeaderEntryPointButton.heart.fill
  const buttonText = isInternational ? (
    <FormattedMessage
      defaultMessage='Contact us'
      description='Label of button that takes the user to the page to get in touch with a Care Navigator'
    />
  ) : (
    <FormattedMessage
      defaultMessage='Get in touch'
      description='Label of button that takes the user to the page to get in touch with a Care Navigator'
    />
  )

  const defaultHeadphoneIcon = <HeadphoneHeartIcon fill={buttonColorIcon} heartFill={heartFillColor} />

  /* CN icon wrapped in tooltip on web. The icon button is not a child of Tooltip in this case because of accessibility
     violations from nesting interactive elements, hence the absolute positioning. On mobile screens the tooltip is not rendered */
  const iconButtonWithWebTooltip = (
    <>
      {IS_WEB && breakpoints.isMinWidthTablet && (
        <Tooltip
          ref={tooltipRef}
          content={buttonText}
          hoverEnabled={true}
          style={IS_WEB ? { outline: 'none', position: 'absolute', top: '100%', right: '50%', marginTop: 40 } : {}}
        >
          <></>
        </Tooltip>
      )}
      {isAuroraTheme && lightBgColor ? (
        <SecondaryIconButton
          testID={testID}
          onPress={onPress}
          leftIcon={<AuroraIconCircle>{defaultHeadphoneIcon}</AuroraIconCircle>}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Care Navigator',
            description: 'Care navigator entry point',
          })}
          size={ButtonSize.DEFAULT}
          onHoverIn={() => {
            tooltipRef.current?.show()
            onIconHover({ hoverText: isInternational ? 'Contact us' : 'Get in touch' }) // Track hover in event for tooltip
          }}
          onHoverOut={() => tooltipRef.current?.hide()}
          {...restProps}
          style={{
            padding: 0,
          }}
        />
      ) : (
        <TertiaryIconButton
          testID={testID}
          onPress={onPress}
          leftIcon={isAuroraTheme ? <AuroraIconCircle>{defaultHeadphoneIcon}</AuroraIconCircle> : defaultHeadphoneIcon}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Care Navigator',
            description: 'Care navigator entry point',
          })}
          size={ButtonSize.SMALL}
          onHoverIn={() => {
            tooltipRef.current?.show()
            onIconHover({ hoverText: isInternational ? 'Contact us' : 'Get in touch' }) // Track hover in event for tooltip
          }}
          onHoverOut={() => tooltipRef.current?.hide()}
          {...restProps}
          style={
            isAuroraTheme
              ? {
                  padding: 0,
                }
              : {}
          }
        />
      )}
    </>
  )

  return !breakpoints.isMinWidthTablet || useSimplifiedCNIconWithTooltip ? (
    iconButtonWithWebTooltip
  ) : (
    <StyledBaseButton
      selected={isActive}
      testID={testID}
      onPress={onPress}
      text={buttonText}
      leftIcon={<HeadphoneHeartIcon fill={buttonColorIcon} heartFill={heartFillColor} />}
      customTextColor={buttonColorText}
      buttonType={ButtonType.TERTIARY_HEADER}
      {...restProps}
    />
  )
}
